import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { ToastProvider } from './contexts/ToastContext';
import { RequireAdmin, RequireClient, RequireGuest } from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingScreen from './components/LoadingScreen';
import ApiToastBridge from './components/ApiToastBridge';

// Layouts — loaded eagerly (always needed)
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './layouts/AdminLayout';
import ClientLayout from './layouts/ClientLayout';
import PublicLayout from './layouts/PublicLayout';

// Auth pages — loaded eagerly (first pages users see)
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Lazy-loaded auth pages
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const TwoFactorChallengePage = lazy(() => import('./pages/auth/TwoFactorChallengePage'));

// Shared pages
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Admin pages
const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const ClientListPage = lazy(() => import('./pages/admin/clients/ClientListPage'));
const ClientFormPage = lazy(() => import('./pages/admin/clients/ClientFormPage'));
const ClientDetailPage = lazy(() => import('./pages/admin/clients/ClientDetailPage'));
const ProjectListPage = lazy(() => import('./pages/admin/projects/ProjectListPage'));
const ProjectFormPage = lazy(() => import('./pages/admin/projects/ProjectFormPage'));
const ProjectDetailPage = lazy(() => import('./pages/admin/projects/ProjectDetailPage'));
const EnvironmentListPage = lazy(() => import('./pages/admin/environments/EnvironmentListPage'));
const EnvironmentDetailPage = lazy(() => import('./pages/admin/environments/EnvironmentDetailPage'));
const MaintenancePage = lazy(() => import('./pages/admin/maintenance/MaintenancePage'));
const TicketListPage = lazy(() => import('./pages/admin/tickets/TicketListPage'));
const TicketDetailPage = lazy(() => import('./pages/admin/tickets/TicketDetailPage'));
const BillingPage = lazy(() => import('./pages/admin/billing/BillingPage'));
const ReportsPage = lazy(() => import('./pages/admin/reports/ReportsPage'));
const HealthPage = lazy(() => import('./pages/admin/health/HealthPage'));
const UsersPage = lazy(() => import('./pages/admin/users/UsersPage'));
const SettingsPage = lazy(() => import('./pages/admin/settings/SettingsPage'));
const SslPage = lazy(() => import('./pages/admin/ssl/SslPage'));
const SlaPage = lazy(() => import('./pages/admin/sla/SlaPage'));
const RunbooksPage = lazy(() => import('./pages/admin/runbooks/RunbooksPage'));
const CustomTasksPage = lazy(() => import('./pages/admin/custom-tasks/CustomTasksPage'));
const NotificationRulesPage = lazy(() => import('./pages/admin/notification-rules/NotificationRulesPage'));
const IntegrationsPage = lazy(() => import('./pages/admin/integrations/IntegrationsPage'));
const CannedResponsesPage = lazy(() => import('./pages/admin/canned-responses/CannedResponsesPage'));
const ContentPage = lazy(() => import('./pages/admin/content/ContentPage'));
const AdminServiceRequestsPage = lazy(() => import('./pages/admin/service-requests/ServiceRequestsPage'));
const BackupsPage = lazy(() => import('./pages/admin/backups/BackupsPage'));

// Client pages
const ClientDashboardPage = lazy(() => import('./pages/client/DashboardPage'));
const ClientProjectListPage = lazy(() => import('./pages/client/projects/ProjectListPage'));
const ClientProjectDetailPage = lazy(() => import('./pages/client/projects/ProjectDetailPage'));
const ClientTicketListPage = lazy(() => import('./pages/client/tickets/TicketListPage'));
const ClientTicketCreatePage = lazy(() => import('./pages/client/tickets/TicketCreatePage'));
const ClientTicketDetailPage = lazy(() => import('./pages/client/tickets/TicketDetailPage'));
const ClientBillingPage = lazy(() => import('./pages/client/billing/BillingPage'));
const ClientReportsPage = lazy(() => import('./pages/client/reports/ReportsPage'));
const ClientMaintenancePage = lazy(() => import('./pages/client/maintenance/MaintenancePage'));
const HelpCenterPage = lazy(() => import('./pages/client/help/HelpCenterPage'));
const ClientServiceRequestsPage = lazy(() => import('./pages/client/service-requests/ServiceRequestsPage'));
const TeamPage = lazy(() => import('./pages/client/team/TeamPage'));
const ApiTokensPage = lazy(() => import('./pages/client/api-tokens/ApiTokensPage'));
const NotificationsPage = lazy(() => import('./pages/client/notifications/NotificationsPage'));
const OnboardingPage = lazy(() => import('./pages/client/onboarding/OnboardingPage'));

// Public pages
const HomePage = lazy(() => import('./pages/public/HomePage'));
const PublicBlogPage = lazy(() => import('./pages/public/BlogPage'));
const PublicBlogPostPage = lazy(() => import('./pages/public/BlogPostPage'));
const PublicChangelogPage = lazy(() => import('./pages/public/ChangelogPage'));
const PublicKnowledgeBasePage = lazy(() => import('./pages/public/KnowledgeBasePage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const StatusPage = lazy(() => import('./pages/public/StatusPage'));

function SuspenseFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[var(--color-primary)]" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <ToastProvider>
          <ApiToastBridge />
          <AuthProvider>
            <ErrorBoundary>
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {/* Public / Guest routes */}
                  <Route element={<RequireGuest />}>
                    <Route element={<AuthLayout />}>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                    </Route>
                  </Route>

                  {/* 2FA challenge */}
                  <Route element={<AuthLayout />}>
                    <Route path="/2fa-challenge" element={<TwoFactorChallengePage />} />
                  </Route>

                  {/* Public pages (no auth required) */}
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<Suspense fallback={<SuspenseFallback />}><HomePage /></Suspense>} />
                    <Route path="/blog" element={<Suspense fallback={<SuspenseFallback />}><PublicBlogPage /></Suspense>} />
                    <Route path="/blog/:slug" element={<Suspense fallback={<SuspenseFallback />}><PublicBlogPostPage /></Suspense>} />
                    <Route path="/changelog" element={<Suspense fallback={<SuspenseFallback />}><PublicChangelogPage /></Suspense>} />
                    <Route path="/kb" element={<Suspense fallback={<SuspenseFallback />}><PublicKnowledgeBasePage /></Suspense>} />
                    <Route path="/pricing" element={<Suspense fallback={<SuspenseFallback />}><PricingPage /></Suspense>} />
                    <Route path="/contact" element={<Suspense fallback={<SuspenseFallback />}><ContactPage /></Suspense>} />
                    <Route path="/status/:token" element={<Suspense fallback={<SuspenseFallback />}><StatusPage /></Suspense>} />
                  </Route>

                  {/* Admin routes */}
                  <Route element={<RequireAdmin />}>
                    <Route element={<AdminLayout />}>
                      <Route path="/admin" element={<Suspense fallback={<SuspenseFallback />}><AdminDashboardPage /></Suspense>} />
                      <Route path="/admin/profile" element={<Suspense fallback={<SuspenseFallback />}><ProfilePage /></Suspense>} />

                      <Route path="/admin/clients" element={<Suspense fallback={<SuspenseFallback />}><ClientListPage /></Suspense>} />
                      <Route path="/admin/clients/create" element={<Suspense fallback={<SuspenseFallback />}><ClientFormPage /></Suspense>} />
                      <Route path="/admin/clients/:id" element={<Suspense fallback={<SuspenseFallback />}><ClientDetailPage /></Suspense>} />
                      <Route path="/admin/clients/:id/edit" element={<Suspense fallback={<SuspenseFallback />}><ClientFormPage /></Suspense>} />

                      <Route path="/admin/projects" element={<Suspense fallback={<SuspenseFallback />}><ProjectListPage /></Suspense>} />
                      <Route path="/admin/projects/create" element={<Suspense fallback={<SuspenseFallback />}><ProjectFormPage /></Suspense>} />
                      <Route path="/admin/projects/:id" element={<Suspense fallback={<SuspenseFallback />}><ProjectDetailPage /></Suspense>} />
                      <Route path="/admin/projects/:id/edit" element={<Suspense fallback={<SuspenseFallback />}><ProjectFormPage /></Suspense>} />

                      <Route path="/admin/environments" element={<Suspense fallback={<SuspenseFallback />}><EnvironmentListPage /></Suspense>} />
                      <Route path="/admin/environments/:id" element={<Suspense fallback={<SuspenseFallback />}><EnvironmentDetailPage /></Suspense>} />

                      <Route path="/admin/maintenance" element={<Suspense fallback={<SuspenseFallback />}><MaintenancePage /></Suspense>} />

                      <Route path="/admin/tickets" element={<Suspense fallback={<SuspenseFallback />}><TicketListPage /></Suspense>} />
                      <Route path="/admin/tickets/:id" element={<Suspense fallback={<SuspenseFallback />}><TicketDetailPage /></Suspense>} />

                      <Route path="/admin/billing" element={<Suspense fallback={<SuspenseFallback />}><BillingPage /></Suspense>} />

                      <Route path="/admin/reports" element={<Suspense fallback={<SuspenseFallback />}><ReportsPage /></Suspense>} />

                      <Route path="/admin/health" element={<Suspense fallback={<SuspenseFallback />}><HealthPage /></Suspense>} />

                      <Route path="/admin/ssl" element={<Suspense fallback={<SuspenseFallback />}><SslPage /></Suspense>} />
                      <Route path="/admin/sla" element={<Suspense fallback={<SuspenseFallback />}><SlaPage /></Suspense>} />
                      <Route path="/admin/runbooks" element={<Suspense fallback={<SuspenseFallback />}><RunbooksPage /></Suspense>} />
                      <Route path="/admin/custom-tasks" element={<Suspense fallback={<SuspenseFallback />}><CustomTasksPage /></Suspense>} />
                      <Route path="/admin/notification-rules" element={<Suspense fallback={<SuspenseFallback />}><NotificationRulesPage /></Suspense>} />
                      <Route path="/admin/integrations" element={<Suspense fallback={<SuspenseFallback />}><IntegrationsPage /></Suspense>} />
                      <Route path="/admin/canned-responses" element={<Suspense fallback={<SuspenseFallback />}><CannedResponsesPage /></Suspense>} />
                      <Route path="/admin/content" element={<Suspense fallback={<SuspenseFallback />}><ContentPage /></Suspense>} />
                      <Route path="/admin/service-requests" element={<Suspense fallback={<SuspenseFallback />}><AdminServiceRequestsPage /></Suspense>} />
                      <Route path="/admin/backups" element={<Suspense fallback={<SuspenseFallback />}><BackupsPage /></Suspense>} />

                      <Route path="/admin/users" element={<Suspense fallback={<SuspenseFallback />}><UsersPage /></Suspense>} />

                      <Route path="/admin/settings" element={<Suspense fallback={<SuspenseFallback />}><SettingsPage /></Suspense>} />
                    </Route>
                  </Route>

                  {/* Client routes */}
                  <Route element={<RequireClient />}>
                    <Route element={<ClientLayout />}>
                      <Route path="/client" element={<Suspense fallback={<SuspenseFallback />}><ClientDashboardPage /></Suspense>} />
                      <Route path="/client/profile" element={<Suspense fallback={<SuspenseFallback />}><ProfilePage /></Suspense>} />

                      <Route path="/client/projects" element={<Suspense fallback={<SuspenseFallback />}><ClientProjectListPage /></Suspense>} />
                      <Route path="/client/projects/:id" element={<Suspense fallback={<SuspenseFallback />}><ClientProjectDetailPage /></Suspense>} />

                      <Route path="/client/tickets" element={<Suspense fallback={<SuspenseFallback />}><ClientTicketListPage /></Suspense>} />
                      <Route path="/client/tickets/create" element={<Suspense fallback={<SuspenseFallback />}><ClientTicketCreatePage /></Suspense>} />
                      <Route path="/client/tickets/:id" element={<Suspense fallback={<SuspenseFallback />}><ClientTicketDetailPage /></Suspense>} />

                      <Route path="/client/billing" element={<Suspense fallback={<SuspenseFallback />}><ClientBillingPage /></Suspense>} />

                      <Route path="/client/reports" element={<Suspense fallback={<SuspenseFallback />}><ClientReportsPage /></Suspense>} />

                      <Route path="/client/maintenance" element={<Suspense fallback={<SuspenseFallback />}><ClientMaintenancePage /></Suspense>} />

                      <Route path="/client/help" element={<Suspense fallback={<SuspenseFallback />}><HelpCenterPage /></Suspense>} />

                      <Route path="/client/service-requests" element={<Suspense fallback={<SuspenseFallback />}><ClientServiceRequestsPage /></Suspense>} />
                      <Route path="/client/team" element={<Suspense fallback={<SuspenseFallback />}><TeamPage /></Suspense>} />
                      <Route path="/client/api-tokens" element={<Suspense fallback={<SuspenseFallback />}><ApiTokensPage /></Suspense>} />
                      <Route path="/client/notifications" element={<Suspense fallback={<SuspenseFallback />}><NotificationsPage /></Suspense>} />
                      <Route path="/client/onboarding" element={<Suspense fallback={<SuspenseFallback />}><OnboardingPage /></Suspense>} />
                    </Route>
                  </Route>

{/* 404 */}
                  <Route path="*" element={<Suspense fallback={<SuspenseFallback />}><NotFoundPage /></Suspense>} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </AuthProvider>
        </ToastProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}
